import React, { memo, useRef } from 'react';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { Feature, FeatureCollection } from '@turf/helpers';
import { Alert, Button, Col, Form, ListGroup, OverlayTrigger } from 'react-bootstrap';
import { Formik } from 'formik';
import * as turf from '@turf/turf';
import { useMutation, useQuery } from 'react-apollo';
import { CreateGrower } from '../CreateGrower';
import { getErrorMessage } from '../../../../../../utils/errorUtils';
import { CreateFarm } from '../CreateFarm';
import { calculateAcres, INTERNAL_FIELD_ID, toMultiPolygonGeom, repairGeom, resetRepairIssues, getRepairIssues } from '../../../../utils/mapUtils';
import { Crop, Farm, Field, Grower, Variety  } from '../../../../types/types';
import { useStore } from '../../../../providers/AppProvider';
import * as yup from 'yup';
import { GetFarms, GetGrowers, landscan_fields_insert_input } from '../../../../../../__generated__/types';
import { CREATE_FIELDS, CREATE_FIELD, GET_FARMS, GET_GROWERS2, UPDATE_FIELD } from '../../../../../../queries';
import { useUnitConversion, formatUnitAbbreviation} from '../../../../utils/unitConversion';
import _ from "lodash";

const FIELD_APP_URL = process.env.REACT_APP_FIELD_MGR_ROOT_URL;

type ImportFormProps = {
    features: FeatureCollection | null,
    children?: React.ReactNode,
    onSelectField: Function,
    onHoverField: Function,
    userWarn: string,
    setUserWarn: Function
};

interface IMappings {
    name: string,
    year_planted: string,
    plant_spacing: string,
    row_spacing: string,
    crop: string,
    variety: string,
    area: string,
    area_unit_abbrev?: string,
    cached_shp_acres: string,
    cached_shp_hectares: string
}

type FieldInput = {
    id: string,
    name: string,
    year_planted: string,
    plant_spacing: string,
    row_spacing: string,
    crop: string,
    crop_id: string,
    variety: string,
    acres: string,
    area: string,
    area_unit_abbrev: string,
    cached_shp_acres: string,
    cached_shp_hectares: string,
    grower_id: string,
    farm_id: string,
    invalidGeom?: boolean,
};
const defaultMappings = {
    name: '',
    year_planted: '',
    plant_spacing: '',
    row_spacing: '',
    crop: '',
    variety: '',
    area: '',
    area_unit_abbrev: '',
    cached_shp_acres: '',
    cached_shp_hectares: ''
};

const schema = yup.object().shape({
    name: yup.string().required().max(255),
    year_planted: yup.number(),
    acres: yup.number().positive('a valid block size is required'),
    grower_id: yup.number(),
    farm_id: yup.number(),
    crop_id: yup.number(),//.required('crop is required'),
    variety_id: yup.number(),
    plant_spacing: yup.number().positive('a valid plant spacing is required'),
    row_spacing: yup.number().positive('a valid row spacing is required'),
    url: yup.string().url().max(512)
});/**/

type FieldFormValues = {
    grower_id: string,
    farm_id: string,
    name: string,
    crop_id: string,
    variety_id: string,
    year_planted: string,
    area: string,
    acres: string,
    area_unit_abbrev?: string,
    plant_spacing: string,
    row_spacing: string,
    url: string
};

const ImportForm = ({ children, features, onHoverField, onSelectField, userWarn, setUserWarn }: ImportFormProps) => {  
    const match = useRouteMatch(`${FIELD_APP_URL}/:vp/fields`);
    const history = useHistory();

    const ref = useRef<any>(null);

    const { state: { crops, varieties }, orgId } = useStore();
    const [featureCollection, setFeatureCollection] = useState<FeatureCollection | null>(null);
    const [fieldInputs, setFieldInputs] = useState<FieldInput[]>([]);

    const [currentGrowerFarms, setCurrentGrowerFarms] = useState<Farm[]>([]);
    const [growers, setGrowers] = useState<Grower[]>([]);
    const [currentGrowerId, setCurrentGrowerId] = useState<number | null>(null);
    const [currentGrowerFarm, setCurrentGrowerFarm] = useState<number | null>(null);
    const [farms, setFarms] = useState<Farm[]>([]);

    const [mapping, setMapping] = useState<IMappings>(defaultMappings);
    const [originalFeatures, setOriginalFeatures] = useState<FeatureCollection | null>(null);
    const [repairIssuesState, setRepairIssuesState] = useState<any[]>([]);
    const [mappingInfo, setMappingInfo] = useState<any>({
        grower_id: { name: "", help:""},
        farm_id: { name: "", help:""},
        name: { name: "", help:""},
        crop_id: { name: "", help:""},
        variety_id: { name: "", help:""},
        year_planted: { name: "", help:""},
        area: { name: "", help:"Select the field that contains the block area. Specify the units for this field in the 'Area Unit' field, below. If the input file does not contain area information, this field can be left as 'Calculated from .shp' and the shapefile geometries will be used to calculate the area"},
        area_unit_abbrev: { name: "", help:"If a field is configured in 'Area' drowdown above, then specify the units that values in the 'Area' field represent. If the input file does not contain area information this field can be left as-is."},
        acres: { name: "", help:""},
        plant_spacing: { name: "", help:""},
        row_spacing: { name: "", help:""},
        url: { name: "", help:""}
    });

    const [createFields] = useMutation(CREATE_FIELDS, {
        awaitRefetchQueries: true,
        refetchQueries: [
            'GetFields' // Query name
        ],
        onCompleted: () => { 
            history.push(`${match.url}`);
        }
    });
    const [defaultValues, setDefaultValues] = useState<FieldFormValues>({
        grower_id: "",
        farm_id: "",
        name: "",
        crop_id: "",
        variety_id: "",
        year_planted: "",
        area: "",
        area_unit_abbrev: "",
        acres: "",
        plant_spacing: "",
        row_spacing: "",
        url: ""
    });

    const [showCreateGrower, setShowCreateGrower] = useState<boolean>(false);
    const [showCreateFarm, setShowCreateFarm] = useState<boolean>(false);

    const [preferredAreaMid, setPreferredAreaMid] = useState<any>(null);
    const [preferredAreaAbbr, setPreferredAreaAbbr] = useState<any>("ac");
    const { localePreferences } = useStore();
    const { convertToPreferredUnitValue, convertToPreferredUnitString, unitsConvert } = useUnitConversion();

    const { data: growerData } = useQuery<GetGrowers>(GET_GROWERS2);
    const { data: farmData } = useQuery<GetFarms>(GET_FARMS);

    const setGrowersFarms = (growerId: number) => {
        console.log("072224 growerId", growerId);
        console.log("072224 farms", farms);
        setCurrentGrowerFarms(growerId > 0 ? farms.filter((f: Farm) => f.grower_id === growerId) : [])
    }
    
    useEffect(() => {
        setGrowers(
            growerData
                ? growerData.landscan_growers.map(g => ({
                    id: g.grower_id,
                    name: g.name,
                    description: g.description
                } as Grower))
                : []
        );
    }, [growerData]);

    useEffect(() => {
                setFarms(farmData ? farmData.landscan_farms.map(f => { 
            return {
                id: f.farm_id,
                grower_id: f.grower_id,
                name: f.name,
                description: f.description
            } as Farm
        }) : []);
    }, [farmData]);

    useEffect(() => {
        const _preferredAreaMid = _.get(localePreferences, 'measureUnits.area.mid');
        const _preferredAreaAbbr: string =
            typeof _preferredAreaMid === 'string' ? _preferredAreaMid : _preferredAreaMid?.abbr || 'in';
        setPreferredAreaMid(_preferredAreaMid);
        setPreferredAreaAbbr(_preferredAreaAbbr);
    }, [localePreferences]);

    useEffect(() => {
        // force to update the list of the current grower
        // when the data updates.
        console.log("072224 currentGrowerId", currentGrowerId);

        console.log("072224 farms", farms);
        if(currentGrowerId) {
            setGrowersFarms(currentGrowerId);
        }
    }, [farms]);

    useEffect(() => {
        if (!features) {
            setFeatureCollection(null);
            //setUserWarn("The feature set is empty. Please select a valid shapefile to import.");
          return;
        }
      
        try {
            // Reset the accumulator so issues reflect only this run
            resetRepairIssues();
            setOriginalFeatures(features);
            const repairedFeatures: turf.Feature[] = [];
            for (const f of features.features) {
                // cast to Polygon/MultiPolygon so repairGeom will accept it
                const poly = f as Feature<turf.Polygon | turf.MultiPolygon, any>;
                try {
                    const repaired = repairGeom(poly);
                    console.log("102925 repaired", repaired);
                    if (repaired && repaired.geometry) {
                        // keep original id/props, swap in repaired geometry
                        repairedFeatures.push({
                            ...f,
                            geometry: repaired.geometry as any,
                        } as turf.Feature);
                    } else {
                        console.log("102925 feature with invalid geometry (dropped)", f);
                    }
                } catch (err) {
                    console.error("102925 error repairing geometry", f, err);
                    setUserWarn(
                        getErrorMessage(err)
                            ? `Flawed geometry detected: ${getErrorMessage(err)}`
                            : "An unknown error occurred while processing the shapefile. Please check your file and try again."
                    );
                }
            }

            const cleanedFC: FeatureCollection = {
                ...features,
                features: repairedFeatures,
            };
            // Attach any geometry repair issues for downstream consumption
            (cleanedFC as any).__repairIssues = getRepairIssues();
            setRepairIssuesState((cleanedFC as any).__repairIssues || []);
            console.log("102925 features", features);
            console.log("102925 cleanedFC", cleanedFC);
            setFeatureCollection(cleanedFC);
            // If there are issues, provide a concise user warning
            const issues = (cleanedFC as any).__repairIssues || [];
            if (issues.length > 0) {
                const msg = `${issues.length} geometr${issues.length === 1 ? 'y' : 'ies'} failed to import. Review details in the left panel.`;
                setUserWarn(msg);
            } else {
                setUserWarn("");
            }
        } catch (error) {
            console.log("102925 Error cleaning features:", error);
            setUserWarn(
            getErrorMessage(error)
                ? `Error processing shapefile: ${getErrorMessage(error)}`
                : "An unknown error occurred while processing the shapefile. Please check your file and try again."
            );
        }
    }, [features]);
      

    useEffect(() => {
        console.log("072224 currentGrowerFarms", currentGrowerFarms);
    }, [currentGrowerFarms]);

    useEffect(() => {
        if (!featureCollection) {
            setFieldInputs([]);
            setMapping({ ...defaultMappings });
            return;
        }
        // Build a map of valid inputs by id (from repaired features)
        const validInputs = createInputs(featureCollection);
        const validById = new Map<string, FieldInput>(validInputs.map((fi) => [String(fi.id), fi]));
        const validIdSet = new Set<string>(validInputs.map((fi) => String(fi.id)));

        // Derive the final ordered list from the original feature order when available
        let merged: FieldInput[] = [];
        if (originalFeatures && originalFeatures.features?.length) {
            merged = originalFeatures.features.map((f: Feature) => {
                const id = String(f.id);
                if (validIdSet.has(id)) {
                    return validById.get(id)!;
                }
                // Not in repaired list -> invalid placeholder
                return {
                    id,
                    name: '',
                    crop: '',
                    crop_id: '',
                    year_planted: '',
                    plant_spacing: '',
                    row_spacing: '',
                    variety: '',
                    acres: '',
                    area: '',
                    area_unit_abbrev: preferredAreaAbbr === 'ac' ? 'ac' : 'ha',
                    cached_shp_acres: '',
                    cached_shp_hectares: '',
                    grower_id: '',
                    farm_id: '',
                    invalidGeom: true,
                } as FieldInput;
            });
        } else {
            // Fallback: just use valid inputs if original ordering unavailable
            merged = validInputs;
        }

        setFieldInputs(merged);
        setMapping({ ...defaultMappings });
    }, [featureCollection, originalFeatures, preferredAreaAbbr]);

    const schema = yup.object().shape({
        inputs: yup.array().of(
            yup.object().shape({
                name: yup.string().max(255),//.required('Field name is required').max(255),
                crop_id: yup.string(),//.required('Crop type is required'),
                year_planted: yup.number()
                    .min(1990, 'Year planted must be greater than 1990')
                    .typeError( 'Year planted is set to a non-numeric value'),
                plant_spacing: yup.number().typeError( 'Plant spacing is set to a non-numeric value'),
                row_spacing: yup.number().typeError( 'Row spacing is set to a non-numeric value'),
                // geometry validation
                id: yup.string().test('valid-geom', 'Geometry is not valid.', (value) => {
                    // If we can’t validate yet, don’t block the form.
                    if (!featureCollection || !value) return true;

                    // Find the feature robustly (id may be string/number)
                    const feature = featureCollection.features.find((f: Feature) =>
                    String(f.id) === String(value)
                    ) as Feature | undefined;

                    if (!feature || !feature.geometry) {
                    // Could also choose to `return false` here if you want this to be an error
                    return true;
                    }

                    try {
                    // kinks works on lines; convert polygon→line when necessary
                    const geomType = feature.geometry.type;
                    const inputForKinks =
                        geomType === 'Polygon' || geomType === 'MultiPolygon'
                        ? (turf.polygonToLine(feature as any) as any)
                        : (feature as any);

                    const result = turf.kinks(inputForKinks);
                    return result.features.length === 0;
                    } catch (e) {
                    // Never throw from validation
                    console.warn('kinks validation error:', e);
                    return true;
                    }
                }),
            })
        )
    });


    const getValue = (feature: Feature, key: string): string => {
        if (!feature || !key.length) {
            return '';
        }

        return feature.properties && feature.properties.hasOwnProperty(key) ? feature.properties[key] : '';
    }

    const updateInputsValue = (key: string, value:string) => {
        console.log("D090325 key", key);
        console.log("D090325 value", value);
        console.log("D090325 ref.current.values.inputs", ref.current.values.inputs);
        console.log("D090325 mapping", mapping);
        if (key === "crop"){
            key = "crop_id";
        }
        if (key === "area_unit_abbrev"){
            key = "area";
            value = mapping.area;
        }

        if (!featureCollection ) {
            return;
        }

        // the mapping config has changed. mapping object defines the mapping from the properties
        // in the the feature properties list to the value in the field form. for example, what value
        // in the feature property list should be used for the field name. 

        // make a copy so the current values are maintained. only the column that has been
        // changed in the mapping will get updated.
        if(ref && ref.current) {
            let inputs = JSON.parse(JSON.stringify(ref.current.values.inputs))
            inputs.forEach((input: FieldInput) => {

                console.log("090325 input", input);
                console.log("090325 value", value);
                console.log("090325 key", key);
                console.log("090325 preferredAreaAbbr", preferredAreaAbbr);
                console.log("090325 input.cached_shp_hectares", input.cached_shp_hectares);
                console.log("090325 input.cached_shp_acres", input.cached_shp_acres);
                if (key === "area" && value === "-99") {
                    //const feature = featureCollection.features.find((f: Feature) => f.id === input.id);
                    input[key as keyof IMappings] = preferredAreaAbbr === "ac" ? input.cached_shp_acres : input.cached_shp_hectares;
                    return;
                }
                // Prefer repaired feature; if not found, fall back to original feature to pull properties
                let feature: Feature | undefined = featureCollection.features.find((f: Feature) => String(f.id) === String(input.id));
                if (!feature && originalFeatures) {
                    feature = originalFeatures.features.find((f: Feature) => String(f.id) === String(input.id)) as Feature | undefined;
                }
                if (feature) {
                    console.log("D090325 feature", feature);
                    console.log("D090325 value", value);
                    console.log("D090325 input.area_unit_abbrev", input.area_unit_abbrev);
                    console.log("D090325 mapping", mapping);
                    console.log("D090325 mapping.area_unit_abbrev", mapping.area_unit_abbrev);
                    console.log("D090325 preferredAreaAbbr", preferredAreaAbbr);
                    console.log("D090325 getValue(feature, value)", getValue(feature, value));
                    if (mapping.area_unit_abbrev === preferredAreaAbbr){
                        console.log("D090325 getValue(feature, value)", getValue(feature, value));
                        input[key as keyof IMappings] = getValue(feature, value).toString();
                    }else{
                        const rawVal = getValue(feature, value);
                        const numVal = Number(rawVal);
                        if (!isNaN(numVal)) {
                            console.log("D090325 numVal", numVal);
                            const converted = convertToPreferredUnitValue(numVal, input.area_unit_abbrev, 'area', 'mid');
                            input[key as keyof IMappings] = typeof converted === 'number' ? converted.toString() : `${converted}`;
                        } else {
                            input[key as keyof IMappings] = getValue(feature, value).toString();;
                        }
                        
                    }
                }
            });

            setFieldInputs(inputs);
            if (ref && ref.current && typeof ref.current.setFieldValue === 'function') {
                ref.current.setFieldValue('inputs', inputs, false);
            }
        }
    }

    const getMappingOptions = (feature: Feature): string[] => {
        if (!feature || !feature.properties) {
            return [];
        }

        return Object.keys(feature.properties).filter((k: string) => k !== INTERNAL_FIELD_ID)
    }

    const onMappingChanged = (e: any) => {
        console.log("D090325 e.target.name", e.target.name);
        console.log("D090325 e.target.value", e.target.value);
        setMapping({
            ...mapping, [e.target.name]: e.target.value
        });

        {
            const { name, value } = e.target;
            const runner = () => {
                updateInputsValue(name, value);
                if (ref && ref.current && typeof ref.current.setFieldValue === 'function') {
                    // Keep Formik in sync so values.inputs drives the UI for all rows
                    ref.current.setFieldValue('inputs', ref.current.values.inputs, false);
                }
            };
            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(runner);
            } else {
                setTimeout(runner, 0);
            }
        }
    }

    const createInputs = (collection: FeatureCollection): FieldInput[] => {
        const inputs = collection ? collection.features.map((f: Feature) => {
            const area = turf.area(f.geometry as any); // Calculate area in square meters
            const acres = area * 0.000247105; // Convert square meters to acres
            const hectares = area / 10000;    // Convert square meters to hectares
            return {
                id: f.id as string,
                name: '',
                crop_id: '',
                year_planted: '',
                plant_spacing: '',
                row_spacing: '',
                variety:'',
                acres: acres.toFixed(3).toString(), // Round to 2 decimal places
                area: preferredAreaAbbr === "ac" ? acres.toFixed(3).toString() : hectares.toFixed(3).toString(),
                area_unit_abbrev: preferredAreaAbbr === "ac" ? "ac" : "ha",
                cached_shp_acres: acres.toFixed(3).toString(),
                cached_shp_hectares: hectares.toFixed(9).toString()
            } as FieldInput;
        }) : [];

        return inputs.sort((a: FieldInput, b: FieldInput) => a.id < b.id ? -1 : 1);
    }

    const onMouseEnter = (input: FieldInput) => {
        onHoverField(input.id);
    }

    const onMouseLeave = (input: FieldInput) => {
        onHoverField(null);
    }

    const hasError = (errors: any, index: number, prop: string): boolean => {
        if (!errors && !errors.inputs) {
            return false;
        }

        if (errors.inputs && errors.inputs.length && errors.inputs[index]) {
            return errors.inputs[index].hasOwnProperty(prop);
        }

        return false;
    }

    const hasAnyErrors = (errors: any, index: number): boolean => {
        if (!errors && !errors.inputs) {
            return false;
        }

        return errors.inputs && errors.inputs.length && errors.inputs[index];
    }

    const errorMessage = (errors: any, index: number, prop: string): string => {
        if (!errors && !errors.inputs) {
            return '';
        }

        const {inputs} = errors;

        if (inputs && inputs.length && inputs[index] && inputs[index].hasOwnProperty(prop)) {
            return inputs[index][prop];
        }

        return '';
    }

     const doFieldImport = async (formValues: FieldInput[]) => {
        if(!featureCollection) {
            return;
        }

        // following field values are either entered by the user or mapped from the input source
        // name, year planted, row and plant spacing
        // Only include rows that have a name AND a valid repaired feature geometry
        const validIdSet = new Set<string>(featureCollection.features.map((f: Feature) => String(f.id)));
        const fieldsA = formValues
            .filter((data: FieldInput) => data.name !== "" && data.name !== undefined && data.name !== null)
            .filter((data: FieldInput) => validIdSet.has(String(data.id)));

        const fields = fieldsA.map((data: FieldInput) => {
            const feature = featureCollection.features.find((f: Feature) => String(f.id) === String(data.id));
            const cropId = crops.find((f: any) => f.name.toLowerCase() === data.crop_id.toLowerCase());
            let varietyId = varieties.find((f: any) => f.name.toLowerCase() === data.variety.toLowerCase());
            console.log("A103025 varieties", varieties);
            if (!varietyId) {
                varietyId = varieties.find((f: any) => f.id.toString().toLowerCase() === data.variety.toLowerCase());
            }
            console.log("A103025 varietyId", varietyId);
            const farm = currentGrowerFarm; //currentGrowerFarms.find((f: Farm) => f.name === data.farm_id );
            const grower = currentGrowerId; //growers.find((f: Grower) => f.name === data.grower_id );

            const cachedAcres = Number(data.cached_shp_acres);
            const area = (Number(data.area) > 0 ? Number(data.area) : null) ?? (cachedAcres > 0 ? (preferredAreaAbbr === 'ac' ? cachedAcres : Number(data.cached_shp_hectares)) : null) ?? 0;
            console.log("A103025 data", data);
            console.log("A103025 formValues", formValues);
            console.log("A103025 mapping", mapping);
            console.log("A103025 farm", farm);
            console.log("A103025 grower", grower);

            const toNum = (v: any) => (v === "" || v === null || v === undefined ? undefined : Number(v));
        
            const areaVal = toNum(data.area);
            let acresFromArea: number | undefined;

            if (areaVal != null && Number.isFinite(areaVal)) {
                const unitAbbrOrg = (preferredAreaAbbr || '').toLowerCase();
                const fieldDropdown = mapping.area;
                const inAcres = fieldDropdown === ""
                    ? data.cached_shp_acres // Using .shp calculations
                    : unitAbbrOrg === "ha"
                        ? unitsConvert(areaVal, 'ha', 'ac')
                        : areaVal
                acresFromArea = Math.round(Number(inAcres) * 1000) / 1000;
            } else if (Number.isFinite(cachedAcres) && cachedAcres > 0) {
                acresFromArea = Math.round(cachedAcres * 1000) / 1000;
            } else {
                acresFromArea = undefined;
            }
            console.log("A103025 acresFromArea", acresFromArea);
            (data as any).acresFromArea = acresFromArea;
    


            const plantSpacingNum = toNum(data.plant_spacing);       // undefined if blank
            const rowSpacingNum   = toNum(data.row_spacing);         // undefined if blank
            const areaUnitAbbrev  = preferredAreaAbbr === "ac" ? "ac" : "ha";
            const yearPlantedInt  = toNum(data.year_planted) != null ? parseInt(String(data.year_planted), 10) : undefined;
            console.log("A103025 plantSpacingNum", plantSpacingNum);
            // If you prefer nulls instead of omitting keys, swap undefined → null below.
            // Skip if no feature (shouldn't happen due to pre-filter) or if geometry conversion fails
            if (!feature) {
                console.warn("Skipping submission for row with missing feature:", data.id);
                return undefined as any;
            }

            let geomJson: string | undefined;
            try {
                geomJson = JSON.stringify(toMultiPolygonGeom(feature));
            } catch (e) {
                console.warn("Skipping row due to invalid geometry conversion:", data.id, e);
                return undefined as any;
            }

            const fieldRaw: landscan_fields_insert_input = {
                org_id: orgId,
                field_id: undefined, // omitted below (undefined keys removed)
                name: data.name,
                crop_id: cropId ? cropId.id : null,
                acres: Number.isFinite(acresFromArea) ? acresFromArea : undefined,     // GraphQL expects a number, not "12.34"
                area_unit_abbrev: areaUnitAbbrev || undefined,
                plant_spacing: Number.isFinite(plantSpacingNum as number) ? (plantSpacingNum as number) : undefined,
                row_spacing:   Number.isFinite(rowSpacingNum as number)   ? (rowSpacingNum as number)   : undefined,
                year_planted:  Number.isFinite(yearPlantedInt as number)  ? (yearPlantedInt as number)  : undefined,
                geom: geomJson,
                farm_id: farm ? farm : null,
                grower_id: grower ? grower : null,
                variety_id: varietyId ? varietyId.id : null,
                area: Number.isFinite(area as number) ? (area as number) : undefined,
                // row_spacing_unit_abbrev: rsAbbr || undefined,
                // crop_spacing_unit_abbrev: csAbbr || undefined,
            };
            console.log("A103025 fieldRaw", fieldRaw);
            const clean = <T extends object>(o: T) =>
            Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;

            const field = clean(fieldRaw);
            console.log("A103025 field", field);
            return field;
        });

        const fieldsNonNull = fields.filter(Boolean);

        console.log("A103025 currentGrowerId", currentGrowerId);
        console.log("A103025 formValues", formValues);
        console.log("A103025 fields ----> ", fieldsNonNull);

        // IMPORTANT: must pass a non-null array of non-null items
        console.log("A103025 CREATING FIELD ", { variables: { data: fieldsNonNull } });
        await createFields({ variables: { data: fieldsNonNull } });

     }

    return (
        <div className="d-flex flex-column h-100 overflow-auto">
            <div className="d-flex justify-content-between px-3 py-2">
                <h4>Import Block Boundaries</h4>
            </div>

            <div>
                {features && features.features.length > 0 &&
                    <div className="px-3 py-2">
                        <h5>Data Attributes</h5>
                        <Form.Text className="text-muted mb-2">
                            Select the data attributes from the input file to map to the corresponding block attributes. Leave an entry unselected
                            if a source attribute is not available. Block name and crop type are required attributes.
                        </Form.Text>
                        <div className="border p-3 rounded-lg">
                            {Object.keys(mapping).map((k: string) => (
                                k === "cached_shp_acres" || k === "cached_shp_hectares"
                                ? null 
                                : <div key={k} className="align-items-center d-flex row">
                                    <Form.Label column sm="4" className="text-capitalize d-flex align-items-center">
                                        <span>
                                            {`${k.replace('_', ' ').replace('_abbrev', '')}${
                                                k === "area"
                                                    ? ` (${mapping.area_unit_abbrev || (preferredAreaAbbr === "ac" ? "ac" : "ha")})`
                                                    : ""
                                            }`}
                                        </span>
                                        {mappingInfo?.[k]?.help ? (
                                            <OverlayTrigger
                                                trigger="click"
                                                rootClose
                                                placement="right"
                                                overlay={(overlayProps: any) => (
                                                    <div
                                                        {...overlayProps}
                                                        id={`help-popover-${k}`}
                                                        className="popover bs-popover-right"
                                                        style={{ ...overlayProps.style, maxWidth: 320, zIndex: 1060 }}
                                                        role="dialog"
                                                        aria-modal="true"
                                                    >
                                                        <div className="popover-header d-flex justify-content-between align-items-center">
                                                            <span className="text-capitalize" style={{ color: "black" }}>{`${k.replace('_', ' ').replace('_abbrev', '')}${k === "area" 
                                                                ?  (preferredAreaAbbr === "ac" ? " (ac)" : " (ha)")
                                                                : ""}`}</span>
                                                            <div
                                                                aria-label="Close"
                                                                className="btn btn-sm btn-link p-0"
                                                                style={{ color: "black", textDecoration: 'none', fontSize: '1.2rem', lineHeight: '1rem' }}
                                                                onClick={() => {
                                                                    const t = document.getElementById(`help-trigger-${k}`);
                                                                    if (t) (t as HTMLElement).click();
                                                                }}
                                                            >
                                                                <span aria-hidden="true">&times;</span>
                                                            </div>
                                                        </div>
                                                        <div className="popover-body">
                                                            {`${mappingInfo[k].help}${k === "area_unit_abbrev" ? ` Your orginization will present area data in ${preferredAreaAbbr === "ac" ? "acres" : "hectares"}. If needed, inputs will be transformed to ${preferredAreaAbbr === "ac" ? "acres" : "hectares"} for data storage purposes.` : ""}`}
                                                        </div>
                                                    </div>
                                                )}
                                            >
                                                {({ ref, ...triggerProps }) => (
                                                    <Button
                                                        id={`help-trigger-${k}`}
                                                        ref={ref as any}
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="ml-2 py-0 px-1"
                                                        aria-label="Field help"
                                                        type="button"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        style={{
                                                            lineHeight: 1,
                                                            borderRadius: '50%',
                                                            width: 20,
                                                            height: 20,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            outline: 'none',
                                                            boxShadow: 'none'
                                                        }}
                                                        {...triggerProps}
                                                    >
                                                        i
                                                    </Button>
                                                )}
                                            </OverlayTrigger>
                                        ) : null}
                                    </Form.Label>
                                    <Col sm="8">
                                        <Form.Control
                                            as="select"
                                            className="mt-1"
                                            name={k}
                                            size="sm"
                                            disabled={k === "area_unit_abbrev" && (!mapping.area || mapping.area === "-99")}
                                            value={
                                                k === "area_unit_abbrev"
                                                    ? (mapping.area_unit_abbrev || (preferredAreaAbbr === "ac" ? "ac" : "ha"))
                                                    : mapping[k as keyof IMappings]
                                            }
                                            onChange={(e: any) => {
                                                if (k === "area_unit_abbrev") {
                                                    const val = e.target.value;
                                                    setMapping({ ...mapping, [k]: val });
                                                    if (ref && ref.current) {
                                                        const inputs = JSON.parse(JSON.stringify(ref.current.values.inputs));
                                                        inputs.forEach((input: any) => {
                                                            input.area_unit_abbrev = val;
                                                        });
                                                        setFieldInputs(inputs);
                                                        if (typeof ref.current.setFieldValue === 'function') {
                                                            ref.current.setFieldValue('inputs', inputs, false);
                                                        }
                                                    }
                                                    onMappingChanged(e);
                                                } else {
                                                    onMappingChanged(e);
                                                }
                                            }}
                                        >
                                            {k === "area_unit_abbrev" ? (
                                                <>
                                                    <option value="ac">Acres</option>
                                                    <option value="ha">Hectares</option>
                                                </>
                                            ) : (
                                                <>
                                                    {k === "area" ? (
                                                        <option style={{ fontWeight: 'bold' }} value="-99">Calculated from .shp</option>
                                                    ) : (
                                                        <option value="-1">Select data source</option>
                                                    )}
                                                    {getMappingOptions(features.features[0]).map((key: string) => (
                                                        <option key={key} value={key}>{`${key}`}</option>
                                                    ))}
                                                </>
                                            )}
                                        </Form.Control>
                                    </Col>
                                </div>
                            ))}
                        </div>
                    </div>
                }
            </div>

            {(fieldInputs && fieldInputs.length > 0) &&
                <div className="px-3 py-2">
                    <div className="row px-3">
                        <div className="col-sm-1" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>#</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>Block Name</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>Crop</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>Variety</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>{preferredAreaAbbr === "ac" ? "Acres" : "Hectares"}</strong></div>
                        <div className="col" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}><strong>Year Planted</strong></div>
                    </div>
                </div>
            }
            <CreateGrower isOpen={showCreateGrower} onClose={() => setShowCreateGrower(false)} />
            <CreateFarm isOpen={showCreateFarm} onClose={() => setShowCreateFarm(false)} />
            <div className="flex-grow-1 pt-0 px-3 py-2" style={{flexBasis: 0}}>
                {featureCollection && (featureCollection as any).__repairIssues && (featureCollection as any).__repairIssues.length > 0 && (
                    <Alert variant="danger">
                        <div className="d-flex justify-content-between align-items-center">
                            <strong>
                                {(featureCollection as any).__repairIssues.length} geometries failed to import
                            </strong>
                        </div>
                        <details className="mt-2">
                            <summary>View failed features</summary>
                            <div className="mt-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
                                {(featureCollection as any).__repairIssues.map((iss: any, i: number) => (
                                    <div key={`repair-issue-${i}`} className="mb-2 p-2 border rounded bg-light">
                                        <div><strong>ID:</strong> {String(iss.id ?? 'N/A')}</div>
                                        <div><strong>Reason:</strong> {iss.reason}</div>
                                        {iss.errorMessage && <div><strong>Error:</strong> {iss.errorMessage}</div>}
                                        {iss.polygonIndex != null && <div><strong>Polygon Index:</strong> {iss.polygonIndex}</div>}
                                        <div className="mt-1"><strong>Geometry (GeoJSON):</strong>
                                            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '8pt' }}>{JSON.stringify(iss.geometry || {}, null, 2)}</pre>
                                        </div>
                                        <div className="mt-1"><strong>Properties:</strong>
                                            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '8pt' }}>{JSON.stringify(iss.properties || {}, null, 2)}</pre>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </details>
                    </Alert>
                )}
                <Formik
                    innerRef={ref}
                    validationSchema={schema}
                    enableReinitialize={true}
                    initialValues={{
                        inputs: fieldInputs,
                        grower_id: defaultValues.grower_id,
                        farm_id: defaultValues.farm_id,
                    }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={async (values, { setSubmitting }) => {
                        console.log("E090325 values", values);
                        setSubmitting(true)
                        try {
                            await doFieldImport(values.inputs);
                        } catch(error) {
                            console.log(error);
                        }
                    }}
                >
                    {({
                        values,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                        isSubmitting,
                    }) => {
                        return (
                            <Form noValidate onSubmit={handleSubmit} autoComplete="off" className="d-flex flex-column h-100 justify-content-between">
                                <ListGroup>
                                    {values.inputs.map((f: FieldInput, index: number) => (
                                        //console.log("071924 f", f),
                                        <ListGroup.Item
                                            key={f.id}
                                            as="li"
                                            className="px-3"
                                            action
                                            variant="light"
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={() => onMouseEnter(f)}
                                            onMouseLeave={() => onMouseLeave(f)}>
                                            
                                            <div className="row d-flex align-items-center">
                                                <Col sm="1" style={{paddingLeft:'0px', paddingRight:'0px', textAlign:'center'}}>
                                                    {f.id}
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        type="text"
                                                        name={`inputs[${index}].name`}
                                                        size="sm"
                                                        value={f.name}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="enter block name"
                                                        disabled={!!f.invalidGeom}
                                                    />
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        as="select"
                                                        name={`inputs[${index}].crop_id`}
                                                        size="sm"
                                                        //value={f.crop_id}
                                                        value={
                                                            crops.filter((crop: any) => f.crop_id.toLocaleLowerCase() === crop.name.toLocaleLowerCase()).length 
                                                                ? crops.filter((crop: any) => f.crop_id.toLocaleLowerCase() === crop.name.toLocaleLowerCase())[0].id.toString() 
                                                                : crops.filter((crop: any) => f.crop_id.toLocaleLowerCase() === crop.id.toString().toLocaleLowerCase()).length
                                                                    ? crops.filter((crop: any) => f.crop_id.toLocaleLowerCase() === crop.id.toString().toLocaleLowerCase())[0].id.toString() 
                                                                    : ""
                                                        }
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}          
                                                        disabled={!!f.invalidGeom}
                                                    >
                                                        <option value="" disabled>- select -</option>
                                                        {crops.map((c: Crop) => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </Form.Control>    
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        as="select"
                                                        name={`inputs[${index}].variety`}
                                                        size="sm"
                                                        //value={f.variety}
                                                        value={
                                                            varieties.filter((variety: any) => f.variety.toLocaleLowerCase() === variety.name.toLocaleLowerCase()).length 
                                                                ? varieties.filter((variety: any) => f.variety.toLocaleLowerCase() === variety.name.toLocaleLowerCase())[0].id.toString() 
                                                                : varieties.filter((variety: any) => {
                                                                        //console.log("071924 variety", variety);
                                                                        //console.log("071924 f.variety", f.variety);
                                                                        return f.variety.toLocaleLowerCase() === variety.id.toString().toLocaleLowerCase()
                                                                    }).length
                                                                    ? varieties.filter((variety: any) => f.variety.toLocaleLowerCase() === variety.id.toString().toLocaleLowerCase())[0].id.toString() 
                                                                    : ""
                                                        }
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}          
                                                        disabled={!!f.invalidGeom}
                                                    >
                                                        <option value="" disabled>- select -</option>
                                                        {varieties.map((c: Variety) => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </Form.Control>    
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        type="text"
                                                        name={`inputs[${index}].acres`}
                                                        size="sm"
                                                        value={f.area}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="enter acres value"
                                                        disabled={!!f.invalidGeom}
                                                    />
                                                </Col>
                                                <Col style={{paddingLeft:'0px', paddingRight:'0px'}}>
                                                    <Form.Control
                                                        type="text"
                                                        name={`inputs[${index}].year_planted`}
                                                        size="sm"
                                                        value={f.year_planted}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="enter year planted"
                                                        disabled={!!f.invalidGeom}
                                                    />
                                                </Col>
                                            </div>
                                            {
                                                hasAnyErrors(errors, index) ? (
                                                    <Alert variant="danger" className="mt-3">
                                                         <ul className="mb-0">
                                                        {
                                                            ['id', 'name', 'crop_id', 'variety', 'acres', 'area', 'area_unit_abbrev', 'year_planted', 'row_spacing', 'plant_spacing'].map((p:string) => {
                                                                return hasError(errors, index, p) ? (
                                                                    <li className="is-invalid " key={p}>
                                                                        { errorMessage(errors, index, p)}
                                                                    </li>
                                                                ) : null
                                                            }) 
                                                        }
                                                        </ul>
                                                    </Alert>
                                                ) : null
                                            }
                                            {f.invalidGeom && (
                                                <Alert variant="danger" className="py-1 my-1">
                                                    Invalid geometry – this feature will not be submitted.
                                                </Alert>
                                            )}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                                <Form.Group>
                                    <Form.Label className="d-flex justify-content-between">
                                        Grower
                                    </Form.Label>
                                    <div className="d-flex"> 
                                        <Form.Control
                                            as="select"
                                            name="grower_id"
                                            size="sm"
                                            value={values.grower_id}
                                            onBlur={handleBlur}
                                            isInvalid={!!errors.grower_id}
                                            onChange={e => {
                                                console.log(e);
                                                handleChange(e);
                                                setFieldValue('farm_id', ""); // reset farm
                                                setCurrentGrowerId(e.currentTarget.value ? parseInt(e.currentTarget.value) : null);
                                                setGrowersFarms(e.currentTarget.value ? parseInt(e.currentTarget.value) : -1);
                                            }}
                                        >
                                            <option value="" label="Select a grower" disabled />
                                            {growers.map(option => {
                                                return <option key={option.id} value={option.id}>{option.name}</option>
                                            })}
                                        </Form.Control>
                                        <Button size="sm" className="ml-2" title="Create" onClick={() => setShowCreateGrower(true)}>
                                            +
                                        </Button>
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.grower_id}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label className="d-flex justify-content-between">
                                        Farm
                                    </Form.Label>
                                    <div className="d-flex">
                                        <Form.Control
                                            as="select"
                                            name="farm_id"
                                            size="sm"
                                            value={values.farm_id}
                                            onChange={e => {
                                                console.log(e);
                                                handleChange(e);
                                                setCurrentGrowerFarm(e.currentTarget.value ? parseInt(e.currentTarget.value) : null)
                                            }}
                                            onBlur={handleBlur}
                                            isInvalid={!!errors.farm_id}
                                        >
                                            <option value="" label="Select a farm" disabled />
                                            {currentGrowerFarms.map(option => {
                                                return <option key={option.id} value={option.id}>{option.name}</option>
                                            })}
                                        </Form.Control>
                                        <Button size="sm" className="ml-2" title="Create" onClick={() =>  setShowCreateFarm(true)}>
                                            +
                                        </Button>
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.farm_id}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <div>
                                    <div className="d-flex justify-content-between my-2">
                                        <div className="d-flex gap-2">
                                            <Button type="submit" variant="primary" size="sm" disabled={fieldInputs.length === 0 || isSubmitting}>
                                                Import
                                            </Button>
                                            <Button variant="secondary" size="sm" className="mx-2" onClick={() => {
                                                history.push(`${match.url}`);
                                            }}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
}

export default memo(ImportForm);
